import type { JSX } from 'react';
import type { CategoryDef, DashboardPayload } from '../../dashboard-types.js';
import { cardClassFor, deltaClass, deltaText, severityLabel, type Severity } from '../utils.js';

interface Props {
  data: DashboardPayload;
  sortedCategories: CategoryDef[];
  passesFilter: (c: CategoryDef) => boolean;
  selectedSizes: Set<string>;
  categoryTotal: (c: CategoryDef) => number;
  categoryDelta: (c: CategoryDef) => number;
  sizeTotal: (c: CategoryDef, sz: string) => number;
  sizeDelta: (c: CategoryDef, sz: string) => number;
  severityClass: (v: number) => Severity;
}

/**
 * @graph-stack styling-pattern-watch
 * @graph-domain Styling
 * @graph-business カテゴリ × サイズ タブ。各カテゴリ 1 カード内に選択中サイズごとの型数行（バー + 増減）を並べる
 * @graph-connects none
 */
export function CardsCategorySize(props: Props): JSX.Element {
  const list = props.sortedCategories.filter(props.passesFilter);
  if (list.length === 0) {
    return (
      <div className="cards">
        <p style={{ padding: 20, color: 'var(--text-muted)' }}>該当するカテゴリがありません</p>
      </div>
    );
  }
  return (
    <div className="cards">
      {list.map((c) => {
        const total = props.categoryTotal(c);
        const delta = props.categoryDelta(c);
        const sev = props.severityClass(total);
        const max = Math.max(...props.data.sizes.map((sz) => props.sizeTotal(c, sz)), 1);
        const visibleSizes = props.data.sizes.filter((sz) => props.selectedSizes.has(sz));
        return (
          <div key={c.id} className={`card ${cardClassFor(sev)}`}>
            <div className="card-head">
              <div className="card-head-left">
                <div className={`cat-icon ${c.cls}`}>{c.short}</div>
                <div className="cat-meta">
                  <span className="cat-name">{c.name}</span>
                  <span className="cat-id">category_id: {c.id}</span>
                </div>
              </div>
              <div className="card-summary">
                <span>
                  <b>{total.toLocaleString()}</b>
                  <small> 型</small>
                </span>
                <span className={`delta ${deltaClass(delta)}`}>{deltaText(delta)}</span>
              </div>
            </div>
            <div className="row-list">
              {visibleSizes.length === 0 ? (
                <p style={{ padding: 8, color: 'var(--text-subtle)', fontSize: 12 }}>
                  該当サイズが選択されていません
                </p>
              ) : (
                visibleSizes.map((sz) => {
                  const v = props.sizeTotal(c, sz);
                  const d = props.sizeDelta(c, sz);
                  const rowSev = props.severityClass(v);
                  const pct = max ? Math.round((v / max) * 100) : 0;
                  return (
                    <div key={sz} className={`pat-row ${rowSev}`}>
                      <span className="size-badge">{sz}</span>
                      <div className="pat-bar-wrap">
                        <div className="pat-bar-track">
                          <div className="pat-bar-fill" style={{ width: `${String(pct)}%` }}></div>
                        </div>
                        <div className="pat-bar-meta">
                          <span>カテゴリ内最大比 {pct}%</span>
                          <span>{severityLabel(rowSev)}</span>
                        </div>
                      </div>
                      <div className="pat-count">
                        <b>{v.toLocaleString()}</b>
                        <small>型</small>
                        <span className={`delta ${deltaClass(d)}`}>{deltaText(d)}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div className="card-foot">
              <span className="threshold-info">
                閾値: <code>warn &lt; {props.data.thresholds.warn}</code> /{' '}
                <code>danger &lt; {props.data.thresholds.danger}</code>
              </span>
              <span>サイズ別: {props.data.sizes.length}行</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
